import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { AccordionDetails, Switch } from '@mui/material';
import MuiAccordion from '@mui/material/Accordion';
import IconButton from '@mui/material/IconButton';
import { withStyles } from '@mui/styles';
import FormControlLabel from '@/components/UI/FormControlLabel/FormControlLabel';
import InputText from '@/components/UI/InputText/InputText';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import { fieldTypes, NUMERIC_FIELDS, REQUIRED_SFM_LENGHT_NUMBER, SOFTWARE } from '@/helpers/constants';
import useWarningField from '@/hooks/useWarningField';
import { getCampaignInfo, getCampaignValidations, getIsVANCR } from '@/redux/Campaign/campaign.reducer';
import { getHeaderLanguage } from '@/redux/Header/header.reducer';
import styles from '../Software.module.scss';

const Accordion = withStyles({
  root: {
    width: '100%',
    border: '1px solid rgba(0, 0, 0, .125)',
    boxShadow: 'none',
    '&:not(:last-child)': {
      borderBottom: 0,
    },
    '&:before': {
      display: 'none',
    },
    '&$expanded': {
      margin: 'auto',
    },
  },
  expanded: {},
})(MuiAccordion);

const SfmNumberAccordion = (props) => {
  const { id, sfmNumber, onChange, headerLanguage, validations, validationActive, checkDisabled, canBeDeleted } = props;
  const [expanded, setExpanded] = useState(true);
  const { t } = useTranslation();
  const sfmNumberValidation = validations?.affectedSoftware?.[headerLanguage]?.flashingInformation?.sfm?.[id];
  const warning = useWarningField([SOFTWARE.AFFECTED_SOFTWARE, 'flashingInformation', `sfm[${id}]`], fieldTypes.ACCORDION);
  const errorClass = (warning || (validationActive && sfmNumberValidation?.invalid)) ? 'error' : '';

  return (
    <Accordion expanded={expanded}>
      <div className={styles.AccordionHeader}>
        <div className={styles.TitleContainer}>
          <IconButton id={`open-sfm-${id + 1}`} size="small" onClick={() => setExpanded(!expanded)}>
            <i className={`di icon-pfeil-chevron-${expanded ? 'oben' : 'unten'} ${styles.Icon} `} />
          </IconButton>
          <div className={`${styles.Title} ${errorClass}`}>{`${t('software.sfmNumber')} ${sfmNumber.requestNumber}`}</div>
        </div>
        {canBeDeleted && (
          <SimpleButton
            disabled={checkDisabled}
            size="small"
            color="primary"
            buttonText={t('codeRule.deleteRule')}
            handleClick={() => onChange('delete', id)}
          />
        )}
      </div>
      <AccordionDetails>
        <div className={styles.InputsContainer}>
          <div className={styles.ThreeColumns}>
            <InputText
              type="number"
              disabled={checkDisabled}
              id={NUMERIC_FIELDS.SFM_REQUEST_NO}
              fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'flashingInformation', `sfm[${id}]`, 'requestNumber']}
              otherConditionInvalid={{ and: true, condition: !!sfmNumber.requestNumber }}
              label={t('software.requestNumber')}
              placeholder={t('software.requestNumberPlaceholder',
                { length: t('software.requestNumberPlaceholderLength') })}
              isMandatory
              handleChange={(ev) => onChange('update', id, 'requestNumber', ev.target.value)}
              value={sfmNumber.requestNumber}
              maxLength={REQUIRED_SFM_LENGHT_NUMBER}
              minLength={REQUIRED_SFM_LENGHT_NUMBER}
              invalid={validationActive && sfmNumberValidation?.requestNumber?.invalid}
              helperText={[
                ...(sfmNumberValidation?.requestNumber?.invalid
                  ? [t('validations.sfmNumber', { length: t('software.requestNumberPlaceholderLength') })]
                  : []),
              ]}
              noTranslationField
            />
          </div>
          <div className={styles.ThreeColumns}>
            <FormControlLabel
              disabled={checkDisabled}
              className={styles.FormControlLabel}
              label={(
                <span className={styles.labels}>
                  {t('software.updateTemporaryInlineFlashing')}
                </span>
              )}
              fieldPath={[SOFTWARE.AFFECTED_SOFTWARE, 'flashingInformation', `sfm[${id}]`, 'updateTemporaryInlineFlashing']}
              control={(
                <Switch
                  id={`switch-sfm-number-${id}`}
                  disabled={checkDisabled}
                  checked={sfmNumber.updateTemporaryInlineFlashing}
                  onChange={(ev) => onChange('update', id, 'updateTemporaryInlineFlashing', ev.target.checked)}
                  name="updateTemporaryInlineFlashing"
                  color="primary"
                />
              )}
            />
          </div>
          <div className={styles.ThreeColumns}>
            <div className={styles.LinkMail}>
              {sfmNumber.updateTemporaryInlineFlashing && !checkDisabled && (
                <a href="mailto: inlineflashing@mercedes-benz.com">{t('software.mailUpdateTemporaryInlineFlashing')}</a>
              )}
            </div>
          </div>
        </div>
      </AccordionDetails>
    </Accordion>
  );
};

const mapStateToProps = (state) => ({
  headerLanguage: getHeaderLanguage(state) || getCampaignInfo(state)?.defaultLanguage,
  validations: getCampaignValidations(state),
  validationActive: getCampaignValidations(state)?.active?.all,
  isVANCR: getIsVANCR(state),
});

export default connect(mapStateToProps)(SfmNumberAccordion);
